import React, { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';

interface AdminLayoutProps {
  children: ReactNode;
  fullWidth?: boolean;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, fullWidth = false }) => {
  const { admin, logout } = useAdmin();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    {
      path: '/admin',
      name: 'Dashboard',
      icon: '📊'
    },
    {
      path: '/admin/packages',
      name: 'Package Management',
      icon: '📦'
    },
    {
      path: '/admin/requests',
      name: 'Client Requests',
      icon: '📋'
    },
    {
      path: '/admin/tickets',
      name: 'Support Tickets',
      icon: '🎫'
    },
    {
      path: '/admin/countries',
      name: 'Countries',
      icon: '🌍'
    },
    {
      path: '/admin/users',
      name: 'User Management',
      icon: '👥'
    },
    {
      path: '/admin/email-templates',
      name: 'Email Templates',
      icon: '📧'
    },
    {
      path: '/admin/service-pages',
      name: 'Service Pages',
      icon: '📄'
    },
    {
      path: '/admin/page-builder',
      name: 'Page Builder',
      icon: '🎨'
    },
    {
      path: '/admin/settings',
      name: 'Settings',
      icon: '⚙️'
    }
  ];

  const isActivePath = (path: string) => {
    // Special handling for packages page - also highlight when on template builder/preview routes
    if (path === '/admin/packages') {
      return location.pathname === path ||
             location.pathname.startsWith('/admin/templates/');
    }
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-gray-50" dir="ltr">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Admin Panel</h2>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isActivePath(item.path)
                    ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-700'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <span className="mr-3 text-lg">{item.icon}</span>
                {item.name}
              </Link>
            ))}
          </nav>

          {/* User info and logout */}
          <div className="px-4 py-4 border-t border-gray-200">
            <div className="flex items-center px-4 py-2 text-sm text-gray-600">
              <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center mr-3">
                <span className="text-white font-semibold">
                  {admin?.full_name?.charAt(0) || 'A'}
                </span>
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900">{admin?.full_name}</div>
                <div className="text-xs text-gray-500 capitalize">{admin?.role?.replace('_', ' ')}</div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full mt-2 flex items-center px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 rounded-lg transition-colors"
            >
              <span className="mr-3">🚪</span>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="ml-64">
        {/* Top bar */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-gray-900">
                {navItems.find(item => isActivePath(item.path))?.name || 'Admin Panel'}
              </h1>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500">
                  Welcome back, {admin?.full_name}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <div className={fullWidth ? "p-2" : "p-6"}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;